import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import useUser from '@/hooks/useUser';
import { DQScheduledActionsPage } from '@/types/resources';
import { api } from '@/utils/api';
import { DQActionType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

import WeekdaysField from './WeekdaysField';

const typeOptions = [
  {
    value: DQActionType.START_QUEUE,
    label: 'Enable Queue',
  },
  {
    value: DQActionType.STOP_QUEUE,
    label: 'Disable Queue',
  },
  {
    value: DQActionType.CHANGE_FREQUENCY,
    label: 'Change Frequency',
  },
];

const NetworkInput = styled(Input)`
  text-transform: capitalize;
`;

interface Props {
  onClose: () => void;
  page: DQScheduledActionsPage;
}

export interface FormDataT {
  run_at: string;
  type: DQActionType;
  frequency?: number;
  time?: string;
  start_time?: string;
  is_recurring: boolean;
  weekdays: number[];
}

const NewActionForm: FC<Props> = ({ onClose, page }) => {
  const [network, networkLabel] = useNetwork();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const user = useUser();

  const {
    handleSubmit,
    watch,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      run_at: '',
      type: DQActionType.START_QUEUE,
      is_recurring: false,
      weekdays: [],
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(
      async ({ weekdays, start_time, time, run_at, frequency, ...data }) => {
        try {
          const body = {
            channel_id: page.id,
            network,
            ...(data.type === DQActionType.START_QUEUE &&
              !data.is_recurring && {
                start_time,
              }),
            ...([
              DQActionType.START_QUEUE,
              DQActionType.CHANGE_FREQUENCY,
            ].includes(data.type) && {
              frequency,
            }),
            ...(data.is_recurring
              ? {
                  time,
                  weekdays,
                }
              : {
                  run_at,
                }),
            ...data,
          };

          await api.post(`/v1/editorial/queue/dq-actions`, body);

          await queryClient.invalidateQueries([
            'distribution_queue_pages_and_actions',
            {
              network,
            },
          ]);

          onClose();

          alerts.success('Scheduled action has been successfully created');
        } catch (err) {
          handleFormErrors<FormDataT>(err, setError, alerts, true);
        }
      },
    )(e);
  };

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const type = watch('type');

  const isRecurring = watch('is_recurring');

  return (
    <Modal>
      <SidePanel heading="Add action" onClose={onCancel}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label>Page Name</Label>
                <NetworkInput readOnly disabled value={page.name} />
              </Larger>
              <Larger>
                <Label>Network</Label>
                <NetworkInput readOnly disabled value={networkLabel} />
              </Larger>
              <FormField<FormDataT, DQActionType>
                label="Select Action:"
                type="radio"
                control={control}
                options={typeOptions}
                name="type"
              />
              <FormField<FormDataT, boolean>
                label="Select Type:"
                type="radio"
                inline
                control={control}
                options={[
                  { value: false, label: 'Single' },
                  { value: true, label: 'Recurring' },
                ]}
                name="is_recurring"
              />
              {isRecurring ? (
                <>
                  <WeekdaysField control={control} />
                  <FormField<FormDataT>
                    label="Time"
                    type="datepicker"
                    control={control}
                    datepickerType="time"
                    name="time"
                  />
                </>
              ) : (
                <FormField<FormDataT>
                  label="Action Date & Time"
                  type="datepicker"
                  control={control}
                  name="run_at"
                />
              )}
              {type === DQActionType.START_QUEUE && (
                <>
                  {!isRecurring && (
                    <FormField<FormDataT>
                      label="First Post Date & Time"
                      type="datepicker"
                      control={control}
                      name="start_time"
                    />
                  )}

                  <FormField<FormDataT>
                    label="Frequency"
                    type="duration"
                    minutesOnly
                    control={control}
                    name="frequency"
                  />
                </>
              )}
              {type === DQActionType.CHANGE_FREQUENCY && (
                <FormField<FormDataT>
                  label="Frequency"
                  type="duration"
                  minutesOnly
                  control={control}
                  name="frequency"
                />
              )}
              <Larger>
                <Label>Owner</Label>
                <NetworkInput readOnly disabled value={user!.name} />
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onCancel} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default NewActionForm;
